/**
 * APIクライアント。JWT を localStorage から読み出し付与する。
 */
import { getPreviewResponse, isTodayPreviewMode } from "../lib/preview";

const TOKEN_KEY = "gohan_memo_jwt";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string): void {
  localStorage.setItem(TOKEN_KEY, t);
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (isTodayPreviewMode()) {
    const previewData = await getPreviewResponse<T>(path, init);
    if (previewData !== undefined) {
      return previewData;
    }
  }

  const headers = new Headers(init.headers ?? {});
  const token = getToken();
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const res = await fetch(`/api${path}`, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    if (location.pathname !== "/login") location.replace("/login");
    throw new ApiError(401, "unauthorized", "Unauthorized");
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const err = data?.error ?? { code: "error", message: res.statusText };
    throw new ApiError(res.status, err.code ?? "error", err.message ?? "Error");
  }
  return data as T;
}

async function authorizedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  if (isTodayPreviewMode()) {
    throw new ApiError(
      501,
      "preview_unsupported",
      "Preview mode does not support this request",
    );
  }

  const headers = new Headers(init.headers ?? {});
  const token = getToken();
  if (token) headers.set("authorization", `Bearer ${token}`);
  const res = await fetch(`/api${path}`, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    if (location.pathname !== "/login") location.replace("/login");
    throw new ApiError(401, "unauthorized", "Unauthorized");
  }
  if (!res.ok) {
    const text = await res.text();
    const data = text ? JSON.parse(text) : undefined;
    const err = data?.error ?? { code: "error", message: res.statusText };
    throw new ApiError(res.status, err.code ?? "error", err.message ?? "Error");
  }
  return res;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// ====== 型定義 ======
export interface User {
  id: string;
  name: string;
  email: string;
  daily_target_kcal: number;
  default_daily_target_kcal?: number;
  target_configured: boolean;
}
export interface Food {
  id: string;
  name: string;
  kcal: number;
  use_count: number;
  last_used_at: string | null;
}
export interface MealSetItem {
  id: string;
  food_id: string | null;
  food_name: string;
  kcal: number;
}
export interface MealSet {
  id: string;
  name: string;
  use_count: number;
  last_used_at: string | null;
  items: MealSetItem[];
  total_kcal: number;
}
export interface MediaRef {
  id: string;
}
export interface MealLog {
  id: string;
  eaten_at: string;
  food_name: string;
  kcal: number;
  media: MediaRef[];
}
export interface DayLogs {
  date: string;
  total_kcal: number;
  target_kcal: number;
  min_weight_kg?: number | null;
  logs: MealLog[];
}
export interface DaySummary {
  date: string;
  total_kcal: number;
  target_kcal: number;
  min_weight_kg?: number | null;
}
export interface HistoryOverview {
  total_kcal: number;
  total_target_kcal: number;
}
export interface WeightLog {
  id: string;
  recorded_at: string;
  weight_kg: number;
}

// ====== エンドポイント ======
export const usersApi = {
  me: (date?: string) =>
    api.get<User>(
      `/users/me${date ? `?date=${encodeURIComponent(date)}` : ""}`,
    ),
  update: (
    body: Partial<{
      name: string;
      daily_target_kcal: number;
      target_date: string;
    }>,
  ) => api.patch<User>("/users/me", body),
};

export const foodsApi = {
  list: (q?: string) =>
    api.get<Food[]>(`/foods${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  create: (body: { name: string; kcal: number }) =>
    api.post<Food>("/foods", body),
  update: (id: string, body: { name?: string; kcal?: number }) =>
    api.patch<Food>(`/foods/${id}`, body),
  remove: (id: string) => api.delete<void>(`/foods/${id}`),
};

export const setsApi = {
  list: () => api.get<MealSet[]>("/sets"),
  create: (body: {
    name: string;
    items: { food_id: string | null; food_name: string; kcal: number }[];
  }) => api.post<{ id: string; name: string }>("/sets", body),
  rename: (id: string, name: string) =>
    api.patch<{ id: string; name: string }>(`/sets/${id}`, { name }),
  replaceItems: (
    id: string,
    items: { food_id: string | null; food_name: string; kcal: number }[],
  ) => api.put<void>(`/sets/${id}/items`, { items }),
  remove: (id: string) => api.delete<void>(`/sets/${id}`),
};

export const mealLogsApi = {
  byDate: (date: string) => api.get<DayLogs>(`/meal-logs?date=${date}`),
  range: (from: string, to: string) =>
    api.get<DaySummary[]>(`/meal-logs?from=${from}&to=${to}`),
  historyOverview: () =>
    api.get<HistoryOverview>("/meal-logs/history-overview"),
  create: (body: {
    eaten_at: string;
    food_name: string;
    kcal: number;
    food_id?: string | null;
  }) => api.post<MealLog>("/meal-logs", body),
  fromSet: (setId: string, eaten_at: string) =>
    api.post<{ created: number }>(`/meal-logs/from-set/${setId}`, { eaten_at }),
  copyFromDate: (source_date: string, eaten_at_base: string) =>
    api.post<{ created: number }>(`/meal-logs/copy-from-date`, {
      source_date,
      eaten_at_base,
    }),
  update: (id: string, body: { eaten_at: string }) =>
    api.patch<MealLog>(`/meal-logs/${id}`, body),
  remove: (id: string) => api.delete<void>(`/meal-logs/${id}`),
};

export const mediaApi = {
  createUploadUrl: (body: { meal_log_id: string; content_type?: string }) =>
    api.post<{ media_id: string; upload_url: string; storage_key: string }>(
      "/media/upload-url",
      body,
    ),
  upload: async (id: string, body: Blob) => {
    await authorizedFetch(`/media/${id}/upload`, {
      method: "PUT",
      headers: {
        "content-type": body.type || "application/octet-stream",
      },
      body,
    });
  },
  confirm: (id: string) => api.patch<{ ok: true }>(`/media/${id}/confirm`),
  get: async (id: string) => {
    const res = await authorizedFetch(`/media/${id}/file`, { method: "GET" });
    return res.blob();
  },
  remove: (id: string) => api.delete<void>(`/media/${id}`),
};

export const weightLogsApi = {
  range: (from: string, to: string) =>
    api.get<WeightLog[]>(`/weight-logs?from=${from}&to=${to}`),
  create: (body: { recorded_at: string; weight_kg: number }) =>
    api.post<WeightLog>("/weight-logs", body),
  remove: (id: string) => api.delete<void>(`/weight-logs/${id}`),
};
